'use client';
import React, { useRef, useEffect, useState } from 'react';

interface Course {
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  featured?: boolean;
}

const courses: Course[] = [
  {
    icon: '🌸',
    title: 'Reiki',
    subtitle: 'Basic to Advance',
    description: 'Master the ancient Japanese healing art of Reiki — from foundational energy channeling to advanced healing techniques and attunements.',
    featured: true,
  },
  {
    icon: '🔥',
    title: 'Lama Fera',
    subtitle: 'Basic to Advance',
    description: 'Learn this powerful Buddhist healing technique used by Lamas for centuries — from basic healing to advanced energy work and spiritual protection.',
    featured: true,
  },
  {
    icon: '🧘',
    title: 'Self Hypnosis',
    subtitle: 'Certification Course',
    description: 'Harness the power of your subconscious mind through self-hypnosis techniques for healing, manifestation, and personal transformation.',
  },
  {
    icon: '💜',
    title: 'Violet Flame Reiki',
    subtitle: 'Certification Course',
    description: 'Work with the transformative violet flame energy to transmute negative karma, purify the aura, and accelerate spiritual evolution.',
  },
  {
    icon: '🔺',
    title: 'Shri Yantra Healing',
    subtitle: 'Certification Course',
    description: 'Unlock the sacred geometry of the Shri Yantra for abundance, prosperity, and spiritual awakening through this ancient Vedic healing system.',
  },
  {
    icon: '👼',
    title: 'Angel Healing',
    subtitle: 'Certification Course',
    description: 'Connect with angelic realms and learn to channel divine healing energy through angel communication, oracle guidance, and celestial healing.',
  },
  {
    icon: '🕉️',
    title: 'Humkara with Haleem & Buddhikara',
    subtitle: 'Advanced Workshop',
    description: 'A profound spiritual practice combining sacred sound healing, divine wisdom, and advanced energy techniques for deep transformation.',
    featured: true,
  },
];

function CourseCard({ course, index }: { course: Course; index: number }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTimeout(() => setVisible(true), index * 80);
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={ref}
      className={`relative rounded-2xl p-6 flex flex-col h-full transition-all duration-700 hover:scale-[1.02] ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{
        background: course.featured
          ? 'linear-gradient(135deg, rgba(201,168,76,0.10) 0%, rgba(124,92,191,0.14) 100%)'
          : 'rgba(22, 12, 40, 0.75)',
        border: course.featured ? '1px solid rgba(201,168,76,0.40)' : '1px solid rgba(155,127,232,0.22)',
        boxShadow: course.featured ? '0 4px 30px rgba(201,168,76,0.12)' : '0 4px 20px rgba(0,0,0,0.2)',
      }}
    >
      {course.featured && (
        <div className="absolute top-3 right-3">
          <span className="text-[10px] tracking-widest uppercase text-[#c9a84c] border border-[#c9a84c]/40 px-2 py-0.5 rounded-full bg-[#c9a84c]/10">
            Featured
          </span>
        </div>
      )}

      <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-[#c9a84c]/30 rounded-tl-xl" />
      <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-[#c9a84c]/30 rounded-br-xl" />

      <div className="text-3xl mb-3">{course.icon}</div>
      <h3 className="font-serif text-xl font-semibold text-[#e8dff5] mb-1">{course.title}</h3>
      <p
        className="text-xs tracking-widest uppercase mb-3 font-medium"
        style={{
          background: 'linear-gradient(135deg, #c9a84c, #e8c96a)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {course.subtitle}
      </p>
      <p className="text-[#c4b5fd]/65 text-sm leading-relaxed flex-1 mb-5">{course.description}</p>

      <a
        href={`https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20join%20the%20${encodeURIComponent(course.title + ' - ' + course.subtitle)}%20course`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-xs font-semibold tracking-widest uppercase transition-all duration-300 hover:scale-105"
        style={{
          background: 'linear-gradient(135deg, #c9a84c 0%, #e8c96a 100%)',
          color: '#0d0818',
          boxShadow: '0 4px 15px rgba(201,168,76,0.3)',
        }}
      >
        Join Now ✨
      </a>
    </div>
  );
}

export default function CoursesSection() {
  const [titleVisible, setTitleVisible] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="courses"
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #130a24 0%, #1a0d30 50%, #130a24 100%)' }}
    >
      <div className="absolute inset-0 lotus-bg opacity-35 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 glow-orb-gold" style={{ width: '500px', height: '500px', opacity: 0.3 }} />
        <div className="absolute bottom-0 left-0 glow-orb-lavender" style={{ width: '400px', height: '400px', opacity: 0.35 }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1000 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
            <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">Signature Programs</span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
          </div>
          <h2
            className="font-serif text-4xl md:text-5xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Signature Courses & Workshops
          </h2>
          <div className="section-divider mb-4" />
          <p className="text-[#c4b5fd]/65 max-w-2xl mx-auto text-base leading-relaxed">
            Transform your life and help others heal. Learn powerful healing modalities from a certified master with 10+ years of experience.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {courses.map((course, index) => (
            <CourseCard key={course.title} course={course} index={index} />
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-[#c4b5fd]/50 text-sm mb-4">Want to know more about our courses and upcoming batches?</p>
          <a
            href="https://wa.me/917049753125?text=Hello%2C%20I%20would%20like%20to%20know%20more%20about%20your%20courses%20and%20workshops"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-gold inline-block"
          >
            Enquire About Courses
          </a>
        </div>
      </div>
    </section>
  );
}
